import { BellIcon } from "lucide-react";
import { Fragment } from "react";

import { MobilePushSettingsSection } from "./MobilePushSettingsSection";
import { NotificationChannelSelector } from "./NotificationChannelSelector";
import {
  Column,
  EmptyState,
  ErrorPlaceholder,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { Label } from "@/components/ui/label";

import { useGetUserNotificationPreferences } from "@/hooks/queries/useGetUserNotificationPreferences";
import { useUpdateSetUserNotificationPreference } from "@/hooks/updateSets/useUpdateSetUserNotificationPreference";
import {
  NOTIFICATION_CATALOG,
  notificationCategoryToDescription,
  notificationCategoryToMeta,
  notificationPreferenceGroupToLabel,
  notificationTypeToLabel,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  NotificationCategoryId,
  NotificationChannel,
  NotificationPreferenceGroup,
  NotificationType,
  UserNotificationPreferenceResponse,
} from "@/types";

const NOTIFICATION_CATEGORY_IDS = Object.values(NotificationCategoryId);

export const NotificationsSettingsContent = ({
  onPreferenceChange,
}: {
  onPreferenceChange: () => void;
}) => {
  const {
    data: preferences = [],
    isPending,
    isError,
  } = useGetUserNotificationPreferences();

  // Group preferences by their type (immediate, batched, team) for the UI to be grouped
  const typeToPreferencesMap = new Map(
    preferences.map((preference) => [preference.type, preference]),
  );

  const renderBody = () => {
    if (isPending) {
      return <LoadingIndicator />;
    }
    if (isError) {
      return <ErrorPlaceholder />;
    }
    if (preferences.length === 0) {
      return (
        <EmptyState
          className="py-16"
          description="Notification preferences will appear here."
          Icon={BellIcon}
          title="No preferences yet"
        />
      );
    }
    return (
      <Column className="gap-0 divide-y divide-slate-100">
        {NOTIFICATION_CATEGORY_IDS.map((categoryId) => (
          <Fragment key={categoryId}>
            <NotificationSettingsCategoryRow
              categoryId={categoryId}
              onPreferenceChange={onPreferenceChange}
              typeToPreferencesMap={typeToPreferencesMap}
            />
            {categoryId === NotificationCategoryId.IMMEDIATE ? (
              <MobilePushSettingsSection
                onPreferenceChange={onPreferenceChange}
              />
            ) : null}
          </Fragment>
        ))}
      </Column>
    );
  };

  const render = () => (
    <Column className="min-h-0 flex-1 overflow-y-auto px-12 pb-16 pt-3">
      <Column className="mx-auto w-full max-w-6xl">{renderBody()}</Column>
    </Column>
  );

  return render();
};

const NotificationSettingsCategoryRow = ({
  categoryId,
  onPreferenceChange,
  typeToPreferencesMap,
}: {
  categoryId: NotificationCategoryId;
  onPreferenceChange: () => void;
  typeToPreferencesMap: Map<
    NotificationType,
    UserNotificationPreferenceResponse
  >;
}) => {
  const { Icon, className } = notificationCategoryToMeta[categoryId];

  const typesInCategory = Object.keys(NOTIFICATION_CATALOG)
    .map((type) => type as NotificationType)
    .filter((type) => NOTIFICATION_CATALOG[type]?.category === categoryId);

  /* Helpers */
  // Grouped types are one setting to the user, so they collapse to a single row
  // carried by their first member. Toggling it writes every member.
  const buildRows = () => {
    const rows: { key: string; label: string; type: NotificationType }[] = [];
    const seenGroups = new Set<NotificationPreferenceGroup>();

    for (const type of typesInCategory) {
      const group = NOTIFICATION_CATALOG[type]?.preferenceGroup;
      if (!group) {
        rows.push({
          key: type,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          label: notificationTypeToLabel[type] || "",
          type,
        });
        continue;
      }
      if (seenGroups.has(group)) continue;
      seenGroups.add(group);
      rows.push({
        key: group,
        label: notificationPreferenceGroupToLabel[group],
        type,
      });
    }

    return rows;
  };

  const render = () => (
    <Row className="items-start gap-16 py-16">
      <Column className="w-[240px] shrink-0">
        <Row className="items-start gap-5 py-4">
          <span
            className={cn(
              "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
              className,
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <Column className="items-start gap-1">
            <Label className="text-base font-semibold text-slate-900">
              {notificationCategoryToMeta[categoryId].label}
            </Label>
            <Text className="text-sm font-normal leading-relaxed text-slate-500">
              {notificationCategoryToDescription[categoryId]}
            </Text>
          </Column>
        </Row>
      </Column>

      <Column className="w-full max-w-xl gap-0">
        {buildRows().map((row, index) => (
          <NotificationSettingsTypeRow
            key={row.key}
            hasTopBorder={index > 0}
            label={row.label}
            onPreferenceChange={onPreferenceChange}
            preference={typeToPreferencesMap.get(row.type)}
            type={row.type}
          />
        ))}
      </Column>
    </Row>
  );

  return render();
};

const NotificationSettingsTypeRow = ({
  hasTopBorder,
  label,
  onPreferenceChange,
  preference,
  type,
}: {
  hasTopBorder: boolean;
  label: string;
  onPreferenceChange: () => void;
  preference: UserNotificationPreferenceResponse | undefined;
  type: NotificationType;
}) => {
  const { mutate: updatePreference } = useUpdateSetUserNotificationPreference();
  const catalogEntry = NOTIFICATION_CATALOG[type];

  const handleToggleChannel = (
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    if (!preference) return;
    mixpanelUtils.trackButtonClick(
      "Toggle notification channel",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      { channel, enabled, notification_type: type },
    );
    const request =
      channel === NotificationChannel.IN_APP
        ? { inAppEnabled: enabled }
        : channel === NotificationChannel.EMAIL
          ? { emailEnabled: enabled }
          : { slackEnabled: enabled };
    updatePreference({ id: preference.id, ...request });
    onPreferenceChange();
  };

  if (!preference || !catalogEntry) {
    return null;
  }

  const render = () => (
    <Row
      className={cn(
        "min-h-[80px] items-center justify-between gap-6 py-6",
        hasTopBorder && "border-t border-slate-50",
      )}
    >
      <Column className="min-w-0 flex-1 gap-1">
        <Text className="text-sm font-normal text-slate-700">{label}</Text>
        {catalogEntry.requiresAtLeastOneChannel ? (
          <Row className="flex-wrap items-center gap-2">
            <span className="rounded-md border border-slate-400 px-1.5 py-0.5 text-xs font-medium text-slate-600">
              Required
            </span>
            <Text className="text-xs font-normal text-slate-500">
              This alert can&apos;t be turned off.
            </Text>
          </Row>
        ) : null}
      </Column>
      <NotificationChannelSelector
        onToggleChannel={handleToggleChannel}
        preference={preference}
        requiredChannel={catalogEntry.requiredChannel}
        requiresAtLeastOneChannel={catalogEntry.requiresAtLeastOneChannel}
        unavailableChannel={catalogEntry.unavailableChannel}
      />
    </Row>
  );

  return render();
};
